import React, { useState } from 'react';
import { Form, Input, Button, Typography, Space, notification, Steps, Card } from 'antd';
import { 
    UserOutlined, 
    PhoneOutlined, 
    KeyOutlined, 
    LockOutlined, 
    ArrowRightOutlined,
    CheckCircleOutlined,
    SafetyOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { MAINAPIURL, CUSTOMERNAME } from '../appsettings';
import AuthLayout from './AuthLayout';

const { Text, Title } = Typography;

const ResetPassword = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [memberId, setMemberId] = useState('');
    const [otp, setOtp] = useState('');
    const [phoneHint, setPhoneHint] = useState('');

    // Step 1: Request OTP
    const handleRequestOtp = async (values) => {
        setLoading(true);
        try {
            const response = await axios.post(`${MAINAPIURL}/portal/request_otp.php`, {
                member_id: values.member_id,
                phone: values.phone
            });

            if (response.data.status === 'success') {
                setMemberId(values.member_id);
                setPhoneHint(response.data.phone_hint);
                notification.success({
                    message: 'OTP Sent',
                    description: response.data.message,
                });
                setCurrentStep(1);
            } else {
                notification.error({
                    message: 'Request Failed',
                    description: response.data.message,
                });
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to request OTP. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Verify OTP
    const handleVerifyOtp = async (values) => {
        setLoading(true);
        try {
            const response = await axios.post(`${MAINAPIURL}/portal/verify_otp.php`, {
                member_id: memberId,
                otp: values.otp
            });

            if (response.data.status === 'success') {
                setOtp(values.otp);
                notification.success({
                    message: 'Verified',
                    description: response.data.message,
                });
                setCurrentStep(2);
            } else {
                notification.error({
                    message: 'Verification Failed',
                    description: response.data.message,
                });
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to verify OTP.',
            });
        } finally {
            setLoading(false);
        }
    };

    // Step 3: Reset Password
    const handleResetPassword = async (values) => {
        setLoading(true);
        try {
            const response = await axios.post(`${MAINAPIURL}/portal/reset_password.php`, {
                member_id: memberId,
                otp: otp,
                new_password: values.password
            });

            if (response.data.status === 'success') {
                notification.success({
                    message: 'Success',
                    description: response.data.message,
                });
                setTimeout(() => navigate('/login'), 2000);
            } else {
                notification.error({
                    message: 'Reset Failed',
                    description: response.data.message,
                });
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to reset password.',
            });
        } finally {
            setLoading(false);
        }
    };

    const steps = [
        { title: 'Identity', icon: <UserOutlined /> },
        { title: 'Verify', icon: <SafetyOutlined /> },
        { title: 'New Password', icon: <LockOutlined /> },
    ];

    return (
        <AuthLayout 
            title="Reset Password" 
            subtitle="Follow the steps to regain access to your account."
        >
            <div style={{ marginBottom: 32 }}>
                <Steps 
                    current={currentStep} 
                    size="small" 
                    items={steps}
                    style={{ marginBottom: 24 }}
                />
            </div>

            {currentStep === 0 && (
                <Form layout="vertical" onFinish={handleRequestOtp} requiredMark={false} size="large">
                    <Form.Item
                        name="member_id"
                        label={<Text strong style={{ color: '#555' }}>Member ID</Text>}
                        rules={[{ required: true, message: 'Please enter your Member ID' }]}
                    >
                        <Input prefix={<UserOutlined style={{ color: '#209c96', marginRight: 6 }} />} placeholder="Ex: 12345" style={{ borderRadius: 12 }} />
                    </Form.Item>
                    <Form.Item
                        name="phone"
                        label={<Text strong style={{ color: '#555' }}>Registered Phone Number</Text>}
                        rules={[{ required: true, message: 'Please enter your phone number' }]}
                    >
                        <Input prefix={<PhoneOutlined style={{ color: '#209c96', marginRight: 6 }} />} placeholder="Ex: 0771234567" style={{ borderRadius: 12 }} />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block style={{ height: 50, borderRadius: 12, background: '#209c96', border: 'none', marginTop: 16 }}>
                        Send OTP <ArrowRightOutlined />
                    </Button>
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Link to="/login" style={{ color: '#209c96' }}>Back to Login</Link>
                    </div>
                </Form>
            )}

            {currentStep === 1 && (
                <Form layout="vertical" onFinish={handleVerifyOtp} requiredMark={false} size="large">
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                        <Text type="secondary">OTP sent to {phoneHint}</Text>
                    </div>
                    <Form.Item
                        name="otp"
                        label={<Text strong style={{ color: '#555' }}>Enter 6-Digit OTP</Text>}
                        rules={[
                            { required: true, message: 'Please enter the OTP' },
                            { len: 6, message: 'OTP must be 6 digits' }
                        ]}
                    >
                        <Input prefix={<KeyOutlined style={{ color: '#209c96', marginRight: 6 }} />} placeholder="000000" style={{ borderRadius: 12, textAlign: 'center', letterSpacing: 8, fontSize: 20 }} />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block style={{ height: 50, borderRadius: 12, background: '#209c96', border: 'none', marginTop: 16 }}>
                        Verify OTP <CheckCircleOutlined />
                    </Button>
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Button type="link" onClick={() => setCurrentStep(0)} style={{ color: '#209c96' }}>Change Phone/ID</Button>
                    </div>
                </Form>
            )}

            {currentStep === 2 && (
                <Form layout="vertical" onFinish={handleResetPassword} requiredMark={false} size="large">
                    <Form.Item
                        name="password"
                        label={<Text strong style={{ color: '#555' }}>New Password</Text>}
                        rules={[
                            { required: true, message: 'Please enter a new password' },
                            { min: 8, message: 'Minimum 8 characters' },
                            { pattern: /^\S*$/, message: 'Spaces are not allowed' }
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined style={{ color: '#209c96', marginRight: 6 }} />} placeholder="New Password" style={{ borderRadius: 12 }} />
                    </Form.Item>
                    <Form.Item
                        name="confirm"
                        label={<Text strong style={{ color: '#555' }}>Confirm Password</Text>}
                        dependencies={['password']}
                        rules={[
                            { required: true, message: 'Please confirm your password' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Passwords do not match'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined style={{ color: '#209c96', marginRight: 6 }} />} placeholder="Confirm Password" style={{ borderRadius: 12 }} />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block style={{ height: 50, borderRadius: 12, background: '#209c96', border: 'none', marginTop: 16 }}>
                        Reset Password <CheckCircleOutlined />
                    </Button>
                </Form>
            )}
        </AuthLayout>
    );
};

export default ResetPassword;
